import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import { API } from "@/lib/constants";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (mfaStep && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [mfaStep]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.mfa_required === false && data.user) {
        // Preview environment — MFA skipped, log in directly
        try { window.dispatchEvent(new Event("rex:login")); } catch (_e) {}
        toast.success(`Welcome back, ${data.user.name}!`);
        navigate("/morning-scrum", { state: { user: data.user } });
      } else if (response.ok && data.mfa_required) {
        setMfaToken(data.mfa_token);
        setEmailHint(data.email_hint);
        setMfaStep(true);
        toast.success("Verification code sent to your email");
      } else if (response.ok && data.user_id) {
        try { window.dispatchEvent(new Event("rex:login")); } catch (_e) {}
        toast.success(`Welcome back, ${data.name}!`);
        navigate("/morning-scrum", { state: { user: data } });
      } else {
        toast.error(data.detail || "Invalid email or password");
      }
    } catch (error) {
      toast.error("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste — distribute digits across inputs
      const digits = value.replace(/\D/g, "").slice(0, 6).split("");
      const newOtp = [...otpDigits];
      digits.forEach((d, i) => {
        if (index + i < 6) newOtp[index + i] = d;
      });
      setOtpDigits(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      // Auto-submit if all filled
      if (newOtp.every(d => d !== "")) {
        submitOtp(newOtp.join(""));
      }
      return;
    }

    const digit = value.replace(/\D/g, "");
    const newOtp = [...otpDigits];
    newOtp[index] = digit;
    setOtpDigits(newOtp);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && newOtp.every(d => d !== "")) {
      submitOtp(newOtp.join(""));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const submitOtp = async (code) => {
    if (!code || code.length !== 6) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/mfa/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mfa_token: mfaToken, code }),
      });

      const data = await response.json();
      if (response.ok && data.user_id) {
        try { window.dispatchEvent(new Event("rex:login")); } catch (_e) {}
        toast.success(`Welcome back, ${data.name}!`);
        navigate("/morning-scrum", { state: { user: data } });
      } else {
        toast.error(data.detail || "Incorrect code");
        setOtpDigits(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error("Verification failed. Please try again.");
      setOtpDigits(["", "", "", "", "", ""]);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const response = await fetch(`${API}/auth/mfa/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfa_token: mfaToken }),
      });
      if (response.ok) {
        toast.success("New code sent to your email");
        setOtpDigits(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Could not resend code. Please log in again.");
        resetToLogin();
      }
    } catch {
      toast.error("Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const resetToLogin = () => {
    setMfaStep(false);
    setMfaToken("");
    setOtpDigits(["", "", "", "", "", ""]);
  };

  const renderMfaStep = () => (
    <div className="w-full max-w-md space-y-8">
      <div className="text-center">
        <img
          src="https://customer-assets.emergentagent.com/job_669db3fc-fcfc-4e61-838e-67066d41b913/artifacts/gfacdued_REX.png"
          alt="REX"
          className="h-20 mx-auto mb-3"
          data-testid="login-rex-logo"
        />
        <p className="mt-1 text-sm text-slate-500 font-medium">
          RESILIGENT Engine for eXcellence
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 font-['Manrope']">
            Check your email
          </h2>
          <p className="mt-2 text-slate-500 text-sm">
            We sent a 6-digit code to <strong>{emailHint}</strong>
          </p>
        </div>

        <div className="flex justify-center gap-2" data-testid="otp-input-group">
          {otpDigits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={digit}
              onChange={(e) => handleOtpChange(i, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(i, e)}
              className="w-12 h-14 text-center text-xl font-semibold border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
              data-testid={`otp-digit-${i}`}
              disabled={loading}
            />
          ))}
        </div>

        <Button
          onClick={() => submitOtp(otpDigits.join(""))}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
          disabled={loading || otpDigits.some(d => !d)}
          data-testid="mfa-verify-submit"
        >
          {loading ? "Verifying..." : "Verify & Sign In"}
        </Button>

        <div className="flex items-center justify-between text-sm">
          <button
            onClick={resetToLogin}
            className="text-slate-500 hover:text-slate-700 flex items-center gap-1"
            data-testid="mfa-back-to-login"
          >
            <ArrowLeft className="w-3 h-3" /> Back to login
          </button>
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
            data-testid="mfa-resend-code"
          >
            <Mail className="w-3 h-3" /> {resending ? "Sending..." : "Resend code"}
          </button>
        </div>
      </div>

      <p className="text-center text-sm text-slate-400">
        Powered by RESILIGENT &copy; {new Date().getFullYear()}
      </p>
    </div>
  );

  const renderLoginForm = () => (
    <div className="w-full max-w-md space-y-8">
      <div className="text-center">
        <img
          src="https://customer-assets.emergentagent.com/job_669db3fc-fcfc-4e61-838e-67066d41b913/artifacts/gfacdued_REX.png"
          alt="REX"
          className="h-20 mx-auto mb-3"
          data-testid="login-rex-logo"
        />
        <p className="mt-1 text-sm text-slate-500 font-medium">
          RESILIGENT Engine for eXcellence
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-slate-900 font-['Manrope']">
            Welcome back
          </h2>
          <p className="mt-1 text-slate-500">Sign in to your account</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@resiligent.com"
              className="mt-1"
              data-testid="login-email"
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="pr-10"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </div>

      <p className="text-center text-sm text-slate-400">
        Powered by RESILIGENT &copy; {new Date().getFullYear()}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-white">
        {mfaStep ? renderMfaStep() : renderLoginForm()}
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-blue-600 to-blue-800 items-center justify-center p-12">
        <div className="max-w-lg text-white text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_669db3fc-fcfc-4e61-838e-67066d41b913/artifacts/gfacdued_REX.png"
            alt="REX"
            className="h-24 mx-auto mb-4"
            data-testid="login-rex-logo-right"
          />
          <p className="text-blue-200 text-lg mb-6">
            RESILIGENT Engine for eXcellence
          </p>
          <p className="text-xl text-white/90 italic mb-8">
            "Automating your brilliance so you can focus on what matters."
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-2xl font-bold">Sales</div>
              <div className="text-blue-200 text-sm">Clarity & Pipeline</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-2xl font-bold">AI</div>
              <div className="text-blue-200 text-sm">Powered Insights</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-2xl font-bold">Strategic</div>
              <div className="text-blue-200 text-sm">Intelligence</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-2xl font-bold">Disciplined</div>
              <div className="text-blue-200 text-sm">Execution</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
